import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { DeleteWithReason } from '../components/DeleteWithReason';
import { useSession } from '../lib/session';
import type { CompanyDashboard, Priority, PriorityItem, Project } from '../lib/types';
import { PROJECT_STATUS_LABELS, ROLE_LABELS, describeAction, formatMinutes,
  FINISHED_PROJECT_STATUSES,
} from '../lib/types';
import { KindBadge, PriorityBadge, ProjectStatusBadge } from '../components/Badges';
import { CreateProjectModal } from './CreateProjectModal';
import { EditCompanyModal } from './EditCompanyModal';
import { PriorityTable } from './PriorityTable';
import { CategoriesCard } from './CategoriesCard';
import { CompanyPeopleCard } from './CompanyPeopleCard';
import { RatesCard } from './RatesCard';
import { SlaCard } from './SlaCard';
import { formatDate, formatDateTime, todayValue } from '../lib/dates';
import { DataTable } from '../components/DataTable';
import type { Column } from '../components/DataTable';

/** Sorting by name would put "critical" after "low", which is worse than useless. */
const PRIORITY_RANK: Record<Priority, number> = { critical: 0, high: 1, medium: 2, low: 3, none: 4 };

/**
 * The company's projects, as data.
 *
 * Priority sorts by rank rather than by the word on the badge — "critical" after "low" is
 * worse than useless — while the word is what the filter list and the export show.
 */
const PROJECT_COLUMNS: Column<Project>[] = [
  {
    key: 'project',
    header: 'Project',
    width: '30%',
    value: (project) => `${project.code} ${project.name}`,
    filter: 'text',
    render: (project) => (
      <Link to={`/projects/${project.id}`} className="row" style={{ gap: 8 }}>
        <span className="key">{project.code}</span>
        <span style={{ fontWeight: 600 }}>{project.name}</span>
        {project.restricted && <span className="badge">restricted</span>}
      </Link>
    ),
  },
  {
    key: 'priority',
    header: 'Priority',
    value: (project) => (project.priority === 'none' ? '' : project.priority),
    sortValue: (project) => PRIORITY_RANK[project.priority],
    filter: 'choice',
    render: (project) => <PriorityBadge priority={project.priority} />,
  },
  {
    key: 'status',
    header: 'Status',
    value: (project) => PROJECT_STATUS_LABELS[project.status],
    filter: 'choice',
    render: (project) => <ProjectStatusBadge status={project.status} />,
  },
  {
    key: 'target',
    header: 'Target',
    value: (project) => project.targetDate,
    filter: 'none',
    render: (project) => (
      <span
        className={project.overdue ? '' : 'muted'}
        style={project.overdue ? { color: 'var(--accent)' } : undefined}
      >
        {formatDate(project.targetDate)}
      </span>
    ),
  },
  {
    key: 'client',
    header: 'Client',
    value: (project) => (project.sharedWithClient ? 'shared' : 'ours only'),
    filter: 'choice',
    render: (project) => (
      <span className={project.sharedWithClient ? 'badge badge-info' : 'badge'}>
        {project.sharedWithClient ? 'shared' : 'ours only'}
      </span>
    ),
  },
  {
    key: 'repository',
    header: 'Repository',
    value: (project) => project.gitlabProjectPath,
    filter: 'text',
    render: (project) => (
      <span className="dim mono truncate" style={{ maxWidth: 180 }}>
        {project.gitlabProjectPath || '—'}
      </span>
    ),
  },
  {
    key: 'board',
    header: '',
    align: 'right',
    render: (project) => (
      <Link to={`/projects/${project.id}/board`} className="btn btn-sm">
        Board
      </Link>
    ),
  },
];

/**
 * The company dashboard, and where anyone who belongs to exactly one company lands after
 * signing in. Everything about that company in one place: what is in flight, what is stuck,
 * who is carrying it, and what changed lately.
 */
export function CompanyPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { session, refresh } = useSession();
  const [board, setBoard] = useState<CompanyDashboard | null>(null);
  const [priorities, setPriorities] = useState<{ items: PriorityItem[]; withheld: string | null } | null>(null);
  const [time, setTime] = useState<{
    totalMinutes: number;
    billableMinutes: number;
    writtenOffMinutes: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [showFinished, setShowFinished] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    const monthAgo = new Date();
    monthAgo.setDate(monthAgo.getDate() - 30);
    const from = monthAgo.toISOString().slice(0, 10);
    const to = todayValue();

    try {
      const [dashboard, priorityResult] = await Promise.all([
        api.get<CompanyDashboard>(`/companies/${companyId}/dashboard`),
        api.get<{ items: PriorityItem[]; withheld: string | null }>(`/companies/${companyId}/priorities?limit=15`),
      ]);
      setBoard(dashboard);
      setPriorities(priorityResult);
      // Time is a nice-to-have on this page; a company with no time booked must still render.
      api
        .get<{ totalMinutes: number; billableMinutes: number; writtenOffMinutes: number }>(
          `/companies/${companyId}/time?from=${from}&to=${to}`,
        )
        .then(setTime)
        .catch(() => setTime(null));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load this company');
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <div className="callout">{error}</div>;
  if (!board) return <div className="empty">Loading…</div>;

  const { company, totals, capabilities, projects } = board;
  // Anyone who can change work here can ask for it. A read-only role cannot.
  const canRaise = board.capabilities['work.write'] !== 'none' && projects.some((project) => project.status === 'active');

  /*
    Finished projects are hidden by default here too — see the switch on the card below. The
    count is over all of them, so it does not move when the table's own search narrows things.
  */
  const finishedProjects = projects.filter((project) => FINISHED_PROJECT_STATUSES.has(project.status)).length;
  const shownProjects = showFinished ? projects : projects.filter((p) => !FINISHED_PROJECT_STATUSES.has(p.status));
  const canAddProject = capabilities['project.manage'] === 'full';
  const percentDone = totals.tasks === 0 ? 0 : Math.round((totals.done / totals.tasks) * 100);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Dashboard</div>
          <div className="row wrap" style={{ gap: 12 }}>
            <h1>{company.name}</h1>
            <KindBadge kind={company.kind} />
          </div>
          <p className="subtitle">
            <span className="key">{company.code}</span>
            {board.role ? ` · you are ${ROLE_LABELS[board.role].toLowerCase()} here` : ' · administrator view'}
          </p>
        </div>
        <div className="row">
          {/**
           * The primary action for a customer, so it belongs here rather than tucked into a
           * card. Administrators get it too — raising something on a customer's behalf is
           * half of what a service desk does.
           */}
          {canRaise && (
            <Link
              to={`/companies/${companyId}/request`}
              className={board.role === 'client' || board.role === 'client_admin' ? 'btn btn-primary' : 'btn'}
            >
              Raise a request
            </Link>
          )}
          {/* Management's view of this account — ours only, asked by role. */}
          {(session?.person.isAdmin || (board.role && board.role !== 'client' && board.role !== 'client_admin')) && (
            <Link to={`/companies/${companyId}/overview`} className="btn">
              Company overview
            </Link>
          )}
          {/*
            Editing was the one thing the API could always do and the app never offered: a name
            typed wrong, or a contact who has since left, could be entered once and never fixed.
          */}
          {capabilities['company.manage'] === 'full' && (
            <button type="button" className="btn" onClick={() => setEditing(true)}>
              Edit details
            </button>
          )}
          {canAddProject && (
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              Add project
            </button>
          )}
        </div>
      </div>

      <div className="stat-row mb-2">
        <Stat label="Projects" value={projects.length} />
        <Stat label="Tasks" value={totals.tasks} />
        <Stat label="Complete" value={`${percentDone}%`} />
        <Stat label="In progress" value={totals.inProgress} />
        <Stat label="Blocked" value={totals.blocked} accent={totals.blocked > 0} />
        <Stat label="Overdue" value={totals.overdue} accent={totals.overdue > 0} />
        {/*
          The API has returned `billableMinutes` all along and this page asked for it and threw
          it away, so "40 hours" said nothing about whether any of it was chargeable. Shown as
          a second line rather than a second tile: it is a breakdown of the number above it,
          not a number of its own.
        */}
        {time && (
          <Stat
            label="Time, 30 days"
            value={formatMinutes(time.totalMinutes)}
            hint={
              time.totalMinutes > 0
                ? time.writtenOffMinutes > 0
                  ? `${formatMinutes(time.billableMinutes)} billable · ${formatMinutes(time.writtenOffMinutes)} written off`
                  : 'all billable'
                : undefined
            }
            small
          />
        )}
      </div>

      <div className="grid mb-2" style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)' }}>
        <div className="card">
          <div className="card-head">
            <h2>Where the work sits</h2>
            <span className="dim" style={{ fontSize: 12 }}>
              Across every project
            </span>
          </div>
          <div className="card-pad">
            {totals.tasks === 0 ? (
              <p className="dim" style={{ margin: 0 }}>
                No tasks yet.
              </p>
            ) : (
              <div className="stack-sm">
                {board.byStatus.map((status) => (
                  <div key={status.label} className="row" style={{ gap: 10 }}>
                    <span className="dot" style={{ background: status.color }} />
                    <span style={{ minWidth: 96 }}>{status.label}</span>
                    <div className="progress grow">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${totals.tasks === 0 ? 0 : (status.count / totals.tasks) * 100}%`,
                          background: status.color,
                        }}
                      />
                    </div>
                    <span className="mono" style={{ minWidth: 26, textAlign: 'right' }}>
                      {status.count}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {board.byCategory.length > 0 && (
              <>
                <div className="field-label mt-2">What it is about</div>
                <div className="stack-sm">
                  {board.byCategory.slice(0, 6).map((row) => (
                    <div key={row.label} className="row-between" style={{ fontSize: 13 }}>
                      <span className="truncate">{row.label}</span>
                      <span className="mono">{row.count}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {board.byPriority.length > 0 && (
              <>
                <div className="field-label mt-2">Open by priority</div>
                <div className="row wrap" style={{ gap: 7 }}>
                  {board.byPriority.map((row) => (
                    <span key={row.priority} className="row" style={{ gap: 5 }}>
                      <PriorityBadge priority={row.priority} />
                      <span className="mono">{row.count}</span>
                    </span>
                  ))}
                </div>
              </>
            )}

            {totals.unassigned > 0 && (
              <p className="dim" style={{ fontSize: 12, marginBottom: 0, marginTop: 14 }}>
                {totals.unassigned} open {totals.unassigned === 1 ? 'task has' : 'tasks have'} nobody assigned.
              </p>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>{board.byAssignee ? 'Who is carrying it' : 'Activity'}</h2>
          </div>
          <div className="card-pad">
            {board.byAssignee ? (
              board.byAssignee.length === 0 ? (
                <p className="dim" style={{ margin: 0 }}>
                  Nothing is assigned.
                </p>
              ) : (
                <div className="stack-sm">
                  {board.byAssignee.map((person) => (
                    <div key={person.id} className="row-between">
                      <span className="truncate">{person.name}</span>
                      <span className="mono">{person.count}</span>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <p className="dim" style={{ margin: 0, fontSize: 13 }}>
                Who is working on what is our team's own view.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="card mb-2">
        <div className="card-head">
          <h2>Projects</h2>
          <span className="row" style={{ gap: 8, alignItems: 'center' }}>
            {/*
              The same switch the project management page has, and hidden for the same reason
              when there is nothing behind it. A customer with four years of delivered work
              should open this and see what is running, not scroll past what is not.
            */}
            {finishedProjects > 0 && (
              <button
                type="button"
                className={showFinished ? 'btn btn-sm' : 'btn btn-quiet btn-sm'}
                onClick={() => setShowFinished((on) => !on)}
              >
                {showFinished ? 'Hide finished' : `Show ${finishedProjects} finished`}
              </button>
            )}
            <span className="dim" style={{ fontSize: 12 }}>
              Ranked by priority
            </span>
          </span>
        </div>
        {projects.length === 0 ? (
          <div className="empty">
            <h3>No projects yet</h3>
            <p>{canAddProject ? 'Add the first project to give this company a board.' : 'Nothing has been set up here yet.'}</p>
          </div>
        ) : shownProjects.length === 0 ? (
          <div className="empty">
            <h3>Nothing open</h3>
            <p>All {finishedProjects} of their projects have been delivered or archived.</p>
          </div>
        ) : (
          <div className="card-pad">
            <DataTable
              tableKey="company-projects"
              exportName="projects"
              columns={PROJECT_COLUMNS}
              rows={shownProjects}
              searchPlaceholder="Find a project"
            />
          </div>
        )}
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)' }}>
        <div className="card">
          <div className="card-head">
            <h2>What needs attention</h2>
            <span className="row" style={{ gap: 10 }}>
              {/* Every open item for this customer, across their projects, on the Work page. */}
              <Link to={`/work?company=${companyId}`} className="dim" style={{ fontSize: 12 }}>
                See all work
              </Link>
            </span>
          </div>
          <PriorityTable data={priorities} showProject onChanged={load} />
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Lately</h2>
          </div>
          <div className="card-pad">
            {board.recent.length === 0 ? (
              <p className="dim" style={{ margin: 0 }}>
                Nothing has happened yet.
              </p>
            ) : (
              <div className="stack-sm">
                {board.recent.map((event, index) => (
                  <div key={index} style={{ fontSize: 13 }}>
                    <div className="truncate">
                      <span style={{ fontWeight: 600 }}>{event.actor.split(' <')[0]}</span>{' '}
                      <span className="muted">{describeAction(event.action)}</span>{' '}
                      {event.label && <span className="truncate">{event.label}</span>}
                    </div>
                    <div className="dim" style={{ fontSize: 11 }}>
                      {formatDateTime(event.at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/*
        Who has access to this company, for whoever may change it.

        A CLIENT ADMIN's one administrative job, put where their business already is rather
        than on Roles & access, which is ours and lists the whole group. Administrators see the
        same card; the API decides what either of them may actually do with it.
      */}
      {capabilities['people.manage'] !== 'none' && (
        <div className="mt-2">
          <CompanyPeopleCard
            companyId={company.id}
            companyName={company.name}
            isAdmin={!!session?.person.isAdmin}
          />
        </div>
      )}

      <div className="mt-2">
        <CategoriesCard companyId={company.id} canManage={capabilities['company.manage'] === 'full'} />
      </div>

      {/*
        What we have promised them — read-only rather than hidden for a customer: an SLA is a
        promise made TO them, which is the opposite of a rate. Counted over our working hours,
        which are fixed rather than set per company (see backend `working-time.ts`).
      */}
      <div className="mt-2">
        <SlaCard companyId={company.id} />
      </div>

      {/*
        Rates are the most sensitive number in the Hub, so the card is not rendered at all
        rather than rendered read-only — the API refuses it too.
      */}
      {capabilities['company.manage'] === 'full' && (
        <div className="mt-2">
          <RatesCard companyId={company.id} />
        </div>
      )}

      {/*
        Administrators only, and last on the page deliberately — it is the one control here
        that cannot be undone, so it does not sit next to the things people use every day.
      */}
      {session?.person.isAdmin && (
        <div className="mt-2">
          <DeleteWithReason
            what="company"
            name={company.name}
            code={company.code}
            path={`/companies/${company.id}`}
            onDeleted={async () => {
              /* The mirror of creating one: your membership went with it, so the session that
                 still lists it would offer a company nothing can be booked against. */
              await refresh();
              navigate('/companies', { replace: true });
            }}
          />
        </div>
      )}

      {editing && companyId && (
        <EditCompanyModal
          companyId={companyId}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void load();
          }}
        />
      )}

      {creating && companyId && (
        <CreateProjectModal
          companyId={companyId}
          mayBudget={capabilities['company.manage'] === 'full'}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void load();
          }}
        />
      )}
    </>
  );
}

function Stat({
  label,
  value,
  accent,
  small,
  hint,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
  small?: boolean;
  /** A breakdown of the figure above, for a number that means little on its own. */
  hint?: string;
}) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div
        className="stat-value"
        style={{ ...(accent ? { color: 'var(--accent)' } : {}), ...(small ? { fontSize: 20 } : {}) }}
      >
        {value}
      </div>
      {hint && (
        <div className="stat-label" style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
          {hint}
        </div>
      )}
    </div>
  );
}
